/* eslint-disable @typescript-eslint/member-ordering */
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as Sentry from '@sentry/angular';
import { AbilityService } from 'ability';
import jwtDecode from 'jwt-decode';
import { RoutingPath } from 'mbs-ui-shared@constants';
import {
  Administrator,
  BrandingSetting,
  ExtendedOnlineAccessInfo,
  JwtToken,
  OAuthCredential,
  OAuthData,
  TempTokenData
} from 'mbs-ui-shared@models';
import { OAuthCodeParams } from 'mbs-ui-shared@models/OAuthCredential';
import TwoFactorComputers from 'mbs-ui-shared@models/TwoFactorComputers';
import { PassCheckType } from 'mbs-ui-shared@models/auth-models';
import { OneTimeAuthToken } from 'mbs-ui-shared@models/one-time-token.model';
import { AppPersistentStateService, EnvironmentService } from 'mbs-ui-shared@services';
import { AuthOnlineAccessService } from 'mbs-ui-shared@services/auth/auth-online-access.service';
import { AuthService } from 'mbs-ui-shared@services/auth/auth.service';
import { PermissionsService } from 'mbs-ui-shared@services/auth/permissions.service';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  finalize,
  map,
  noop,
  of,
  share,
  switchMap,
  take,
  tap
} from 'rxjs';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class AuthFacade {
  public authError: HttpErrorResponse;

  private meLoaded$ = new BehaviorSubject<boolean>(false);
  private meLoading$ = new BehaviorSubject<boolean>(false);
  private tokenLoading$ = new BehaviorSubject<boolean>(false);

  private currentUserInternal$ = new BehaviorSubject<Administrator>(null);
  private currentBrandingSettingInternal$ = new BehaviorSubject<BrandingSetting>(null);
  private oAuthCredentialInternal$ = new BehaviorSubject<OAuthCredential>(this.authService.getTokenData());
  private twoFAConfirmedInternal$ = new Subject<boolean>();

  public get isAgentLocal(): boolean {
    return this.abilityService.can('read', 'AgentLocal');
  }

  constructor(
    protected environment: EnvironmentService,
    protected authService: AuthService,
    protected abilityService: AbilityService,
    protected appPersistent: AppPersistentStateService,
    protected router: Router,
    protected onlineAccessService: AuthOnlineAccessService
  ) {
    this.initialStreams();
  }

  public twoFAConfirmed = this.twoFAConfirmedInternal$.asObservable();
  public loaded$ = this.meLoaded$.asObservable();
  public loading$ = combineLatest([this.tokenLoading$.asObservable(), this.meLoading$.asObservable()]).pipe(
    map(([tokenLoading, meLoading]) => tokenLoading || meLoading)
  );

  // Online Access part

  public get isMBSMode(): boolean {
    return this.onlineAccessService.isMBSMode();
  }

  public get onlineAccessKey(): string {
    return this.onlineAccessService.getOnlineAccessKey();
  }

  public get onlineAccessInfo$(): Observable<ExtendedOnlineAccessInfo> {
    return this.onlineAccessService.onlineAccessInfo$();
  }

  // Getters part

  public get currentUser$(): Observable<Administrator> {
    return this.currentUserInternal$.asObservable();
  }

  public get authToken(): string {
    const tokenData = this.oAuthCredentialInternal$?.value ?? null;

    return tokenData ? `${tokenData.token_type} ${tokenData.access_token}` : '';
  }

  public get refreshToken(): OAuthCredential {
    return this.oAuthCredentialInternal$.getValue();
  }

  public get fetchCurrentUser$(): Observable<Administrator> {
    return this.fetchUser$();
  }

  public get accessToken(): string {
    return this.oAuthCredentialInternal$?.value?.access_token;
  }

  public get decodedToken(): JwtToken {
    return new JwtToken(jwtDecode(this.accessToken));
  }

  public get currentUserValue(): Administrator {
    return this.currentUserInternal$.value;
  }

  public get subscriptionUrl(): string {
    return this.authService.prepareSubscriptionUrl(this.currentUserValue.ProviderInfo?.Id);
  }

  public get brandingSetting$(): Observable<BrandingSetting> {
    return this.currentBrandingSettingInternal$.asObservable();
  }

  public get authTokenBySession$() {
    return this.authService.authTokenBySessionRequest$().pipe(
      tap((res) => this.oAuthCredentialInternal$.next(res)),
      share()
    );
  }

  // Setters part

  public set credentials(value: OAuthCredential) {
    this.oAuthCredentialInternal$.next(value);
  }

  // Internal part

  private initialStreams(): void {
    this.oAuthCredentialInternal$.pipe(untilDestroyed(this)).subscribe((tokenData) => {
      if (!tokenData) {
        this.appPersistent.data.showAlertAboutBounceList = true;
        this.authService.removeTokenData();
      } else {
        this.authService.setTokenData(tokenData);
      }
    });

    this.appPersistent.change
      .pipe(
        filter((state) => this.currentUserInternal$.value && state.userId !== this.currentUserInternal$.value.Id),
        tap(() => !this.environment.production && this.clearSessionData()),
        switchMap(() => this.fetchUser$()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private fetchUser$(deletePermissions: string[] = []): Observable<Administrator> {
    this.meLoading$.next(true);
    return this.authService.fetchUserRequest$().pipe(
      catchError((error) => {
        this.authError = error;
        this.isMBSMode && this.router.navigate(['AS']);
        this.isAgentLocal && this.router.navigate(['/login']);

        return of(null);
      }),
      map((res) => {
        const user: Administrator = Object.assign(new Administrator(), res);
        this.appPersistent.userId$.emit(user.Id);
        this.appPersistent.resetState();
        if (deletePermissions?.length) {
          deletePermissions.forEach((key) => this.abilityService.deletePermission(key));
        }
        user.Permissions && this.abilityService.update(PermissionsService.handlePermissions(user));
        // emit next user ONLY after the handlePermission operation
        // there are some places where permissions are updated based on some additional parameters
        // it shouldn't be updated after that here
        this.currentUserInternal$.next(user);
        res?.BrandingSetting && this.currentBrandingSettingInternal$.next(res.BrandingSetting);
        this.appPersistent.data.userId = user.Id;
        this.appPersistent.data.signUpDate = user.SignUpDate;

        try {
          // Add a user data for Sentry
          const ownerId = user.ProviderInfo?.Id;
          const scope = Sentry.getGlobalScope();
          scope.setUser({
            username: user.FirstName + user.LastName,
            email: user.Email,
            id: user.Id,
            ownerId
          });
          scope.setExtra('Provider', user?.IsProvider);
          scope.setExtra('Super Admin', user.IsSuperAdmin);
          scope.setExtra('Read Only', user.IsReadOnly);
          scope.setTag('user.ownerId', ownerId);
        } catch (err) {
          noop;
        }
        return user;
      }),
      finalize(() => {
        this.meLoaded$.next(true);
        this.meLoading$.next(false);
      }),
      share()
    );
  }

  // Login part

  public loginRequest$({ email, password }): Observable<boolean> {
    return this.authService.loginRequest$({ email, password });
  }

  public localAgentLogout$(refreshToken: string): Observable<boolean> {
    return this.authService.localAgentLogout$(refreshToken);
  }

  // Token part

  public getOAuthIdentity(params: OAuthCodeParams): Observable<Administrator> {
    this.tokenLoading$.next(true);
    return this.authService.oauthTokenBySessionRequest$(params).pipe(
      tap((res) => this.oAuthCredentialInternal$.next(res)),
      switchMap(() => this.fetchCurrentUser$),
      finalize(() => this.tokenLoading$.next(false))
    );
  }

  public getAuthIdentity(options: OAuthData): Observable<OAuthCredential> {
    this.tokenLoading$.next(true);
    return this.authService.connectTokenRequest$(options).pipe(
      tap((res) => this.oAuthCredentialInternal$.next(res)),
      finalize(() => this.tokenLoading$.next(false))
    );
  }

  public tryRefreshToken$(): Observable<boolean> {
    return this.oAuthCredentialInternal$.asObservable().pipe(
      take(1),
      switchMap((credentials) =>
        credentials
          ? this.refreshToken$().pipe(
              map(() => true),
              catchError(() => of(false))
            )
          : of(false)
      ),
      share()
    );
  }

  public refreshToken$(): Observable<OAuthCredential> {
    if (!this.isMBSMode && !this.isAgentLocal)
      return this.onlineAccessService
        .refreshOnlineAccessToken(this.oAuthCredentialInternal$.value?.refresh_token ?? '')
        .pipe(tap((res) => this.oAuthCredentialInternal$.next(res)));

    // try get access_token from session for external services
    if (this.environment.useSessionToken) {
      return this.authTokenBySession$;
    }

    return this.getAuthIdentity(
      new OAuthData({
        grant_type: 'refresh_token',
        refresh_token: this.oAuthCredentialInternal$.value ? this.oAuthCredentialInternal$.value.refresh_token : ''
      })
    );
  }

  public tempToken$(userId: string, hid = null, target = 'OneTime'): Observable<TempTokenData> {
    const data = { tokenTarget: hid ? target : 'TempInstance', computerHid: hid ?? null };
    return this.isMBSMode ? this.authService.tempTokenRequest$(userId, data) : this.onlineAccessService.tempTokenRequest$(hid, data);
  }

  public oneTimeAuthTokenRequest$(): Observable<OneTimeAuthToken> {
    return this.authService.oneTimeAuthTokenRequest$();
  }

  // Cleaning part

  public clearSessionData(): void {
    this.oAuthCredentialInternal$.next(null);

    if (this.isAgentLocal) this.clearAbilityForAgentLocal();
    else this.abilityService.clear();

    this.currentUserInternal$.next(null);
    this.currentBrandingSettingInternal$.next(null);
    this.meLoaded$.next(false);
  }

  public fetchCurrentUserAndDeletePermissions$(permissions: string[]): Observable<Administrator> {
    return this.fetchUser$(permissions);
  }

  // Two Factor Authentication part

  public requestPermissions(payload: TwoFactorComputers): Observable<boolean> {
    return this.authService.permissionRequest$(payload).pipe(
      map((res) => {
        this.oAuthCredentialInternal$.next(Object.assign({}, this.oAuthCredentialInternal$.value, { access_token: res.access_token }));
        this.twoFAConfirmedInternal$.next(true);
        return true;
      })
    );
  }

  public getDropUserToken(): Observable<OAuthCredential> {
    return this.authService.getDropUserToken();
  }

  public cancelRequestPermission(): void {
    this.twoFAConfirmedInternal$.next(false);
  }

  // Apps part

  public getAppsToken(request: { domain: string }): Observable<{ access_token: string }> {
    return this.authService.appsTokenRequest$(request);
  }

  // Password part

  public checkPasswordStrength(password: string): Observable<PassCheckType> {
    return this.authService.checkPasswordStrengthRequest$(password);
  }

  public getPasswordRules(): Observable<string[]> {
    return this.authService.passwordRulesRequest();
  }

  public generatePassword(): Observable<string> {
    return this.authService.generatePasswordRequest$();
  }

  // Other

  public getFeedBackToken() {
    return this.authService.feedBackTokenRequest$();
  }

  public redirectToSupportPortal(): void {
    window.open(`${location.origin}${RoutingPath.SupportPortal}`, '_blank');
  }

  public changeEmail(value: { email: string; password: string }) {
    return this.authService.changeEmailRequest$(value);
  }

  public allowOfflineEdit(state = true): Observable<null> {
    return this.authService.allowOfflineEditRequest$(state).pipe(switchMap((state) => this.fetchUser$().pipe(map(() => state))));
  }

  public setCompleteGettingStartWizard(): void {
    this.currentUserInternal$.next({ ...this.currentUserInternal$.value, ...{ IsWizardComplete: true } });
  }

  private clearAbilityForAgentLocal(): void {
    const isSimple = this.abilityService.can('read', 'AllowSimpleUi');

    this.abilityService.clear();
    this.abilityService.addRule({ actions: 'read', subject: 'AgentLocal' });

    if (isSimple) this.abilityService.addRule({ actions: 'read', subject: 'AllowSimpleUi' });
  }
}
